import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Mail,
    Search,
    RefreshCcw,
    Users,
    GraduationCap,
    School,
    Phone,
    X,
    Send,
    CheckCircle2,
    AlertCircle,
    Loader2,
} from "lucide-react";
import { getWebinarRegistrations, sendWebinarMail } from "../../utils/webinar";

const ROLE_META = {
    student: { label: "Student", badge: "bg-purple-50 text-purple-700" },
    parent: { label: "Parent", badge: "bg-green-50 text-green-700" },
    teacher: { label: "Teacher", badge: "bg-blue-50 text-[#2148C0]" },
};

const Summary = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
            <Icon size={20} />
        </span>
        <div>
            <p className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold">
                {label}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-0.5">
                {Number(value || 0).toLocaleString()}
            </p>
        </div>
    </div>
);

const WebinarPage = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    const [composeOpen, setComposeOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [banner, setBanner] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const res = await getWebinarRegistrations(token);
            setRegistrations(res?.registrations || []);
        } catch (err) {
            console.error(err);
            setError("Failed to load webinar registrations.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const totals = useMemo(() => {
        const byRole = { student: 0, parent: 0, teacher: 0 };
        registrations.forEach((r) => {
            if (byRole[r.role] !== undefined) byRole[r.role] += 1;
        });
        return { total: registrations.length, ...byRole };
    }, [registrations]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return registrations.filter((r) => {
            if (roleFilter !== "all" && r.role !== roleFilter) return false;
            if (!q) return true;
            return (
                (r.fullName || "").toLowerCase().includes(q) ||
                (r.email || "").toLowerCase().includes(q) ||
                (r.school || "").toLowerCase().includes(q)
            );
        });
    }, [registrations, query, roleFilter]);

    const allFilteredSelected =
        filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));

    const toggleSelectAll = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filtered.forEach((r) => next.delete(r.id));
            } else {
                filtered.forEach((r) => next.add(r.id));
            }
            return next;
        });
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const openCompose = () => {
        setSendError(null);
        setComposeOpen(true);
    };

    const closeCompose = () => {
        if (sending) return;
        setComposeOpen(false);
        setSendError(null);
    };

    const handleSend = (e) => {
        e.preventDefault();
        if (selectedIds.size === 0) return;

        // Non-blocking confirmation via a sonner action toast (was a native prompt).
        toast(`Send this email to ${selectedIds.size} registrant(s)?`, {
            description: subject ? `Subject: ${subject}` : undefined,
            action: { label: "Send", onClick: () => performSend() },
            cancel: { label: "Cancel" },
        });
    };

    const performSend = async () => {
        setSending(true);
        setSendError(null);
        try {
            const token = localStorage.getItem("token");
            const res = await sendWebinarMail(
                { subject, message, ids: Array.from(selectedIds) },
                token
            );
            setBanner({
                type: res.failedCount ? "error" : "success",
                text: res.message || `Sent to ${res.sentCount} registrant(s).`,
            });
            setComposeOpen(false);
            setSubject("");
            setMessage("");
            setSelectedIds(new Set());
        } catch (err) {
            setSendError(err.message || "Failed to send email.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div>
            {/* Hero */}
            <div className="mb-8 bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg shadow-[#2148C0]/20">
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-white/10 rounded-full" />
                <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white text-xs font-medium mb-3">
                            <Mail size={13} /> Webinar Registrations
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                            TeeChaa Webinar
                        </h1>
                        <p className="text-blue-100 mt-1">
                            Everyone who signed up, and a way to email them.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#2148C0] hover:bg-blue-50 text-sm font-semibold shadow"
                    >
                        <RefreshCcw size={15} /> Refresh
                    </button>
                </div>
            </div>

            {banner && (
                <div
                    className={`mb-6 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${banner.type === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                >
                    {banner.type === "success" ? (
                        <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                    ) : (
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    )}
                    <span className="flex-1">{banner.text}</span>
                    <button onClick={() => setBanner(null)} className="opacity-60 hover:opacity-100">
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <Summary icon={Users} label="Total registrants" value={totals.total} color="bg-blue-50 text-[#2148C0]" />
                <Summary icon={GraduationCap} label="Students" value={totals.student} color="bg-purple-50 text-purple-600" />
                <Summary icon={Users} label="Parents" value={totals.parent} color="bg-green-50 text-green-600" />
                <Summary icon={School} label="Teachers" value={totals.teacher} color="bg-amber-50 text-amber-600" />
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name, email or school..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0]"
                    />
                </div>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0]"
                >
                    <option value="all">All roles</option>
                    <option value="student">Students</option>
                    <option value="parent">Parents</option>
                    <option value="teacher">Teachers</option>
                </select>
            </div>

            {/* Selection toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <p className="text-sm text-gray-600">
                    {selectedIds.size > 0
                        ? `${selectedIds.size} selected`
                        : `${filtered.length} registrant${filtered.length === 1 ? "" : "s"}`}
                </p>
                <button
                    onClick={openCompose}
                    disabled={selectedIds.size === 0}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#2148C0] text-white text-sm font-semibold shadow hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#2148C0] transition-colors"
                >
                    <Send size={15} /> Email selected ({selectedIds.size})
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
                        <Loader2 size={18} className="animate-spin" /> Loading registrants...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center">
                        <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No registrants found.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-blue-50/60 text-left">
                                <tr className="text-xs uppercase tracking-[0.14em] text-[#2148C0]">
                                    <th className="px-5 py-3 font-semibold w-10">
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected}
                                            onChange={toggleSelectAll}
                                            className="h-4 w-4 rounded border-gray-300 text-[#2148C0] focus:ring-[#2148C0]/30"
                                        />
                                    </th>
                                    <th className="px-5 py-3 font-semibold">Name</th>
                                    <th className="px-5 py-3 font-semibold">Email</th>
                                    <th className="px-5 py-3 font-semibold">Phone</th>
                                    <th className="px-5 py-3 font-semibold">School</th>
                                    <th className="px-5 py-3 font-semibold">Role</th>
                                    <th className="px-5 py-3 font-semibold">Registered</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => (
                                    <tr key={r.id} className="border-t border-gray-100 hover:bg-blue-50/40">
                                        <td className="px-5 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(r.id)}
                                                onChange={() => toggleSelect(r.id)}
                                                className="h-4 w-4 rounded border-gray-300 text-[#2148C0] focus:ring-[#2148C0]/30"
                                            />
                                        </td>
                                        <td className="px-5 py-3 font-semibold text-gray-900">{r.fullName}</td>
                                        <td className="px-5 py-3 text-gray-700">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Mail size={12} className="text-gray-400" /> {r.email}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-gray-700">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Phone size={12} className="text-gray-400" /> {r.phone}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-gray-700">{r.school}</td>
                                        <td className="px-5 py-3">
                                            <span
                                                className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${ROLE_META[r.role]?.badge || "bg-gray-100 text-gray-700"
                                                    }`}
                                            >
                                                {ROLE_META[r.role]?.label || r.role}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-gray-600">
                                            {r.registered_at ? new Date(r.registered_at).toLocaleString() : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {composeOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D50]/60 backdrop-blur-sm px-4"
                    onClick={closeCompose}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-xl font-bold text-gray-900">
                                Email {selectedIds.size} registrant{selectedIds.size === 1 ? "" : "s"}
                            </h2>
                            <button
                                onClick={closeCompose}
                                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {sendError && (
                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-red-700 text-sm">
                                {sendError}
                            </div>
                        )}

                        <form onSubmit={handleSend} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="e.g. Your TeeChaa Webinar details"
                                    required
                                    disabled={sending}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Message
                                </label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Date, time, and joining link for the webinar..."
                                    required
                                    disabled={sending}
                                    rows={6}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0] resize-none"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeCompose}
                                    disabled={sending}
                                    className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#2148C0] text-white text-sm font-semibold shadow hover:bg-blue-700 disabled:opacity-60"
                                >
                                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                    {sending ? "Sending..." : "Send"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebinarPage;
